import React, {Fragment} from 'react';

import {useHistory} from 'react-router-dom';

import {Box, Divider, Grid} from '@material-ui/core';
import {useSnackbar} from 'notistack';

import {
    BeneficiariesAuthorization,
    BeneficiariesResponse,
    EfeResponse,
    EfeViewsArray,
    SyncClientDataResponse,
} from 'commons/models';
import {ClientService} from 'services';
import {useAlert, useSpinner} from 'hooks';
import {EfeButtonPrimary, EfeModal, EfeOtp} from 'components';
import {useUserContext} from 'context/user/UserContext';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {getRandomHex, nextStep, useViewSearch} from 'commons/utils/Functions';
import BeneficiaryFooter from './views/footer/BeneficiaryFooter';
import BeneficiaryHeader from './views/header/BeneficiaryHeader';
import {deleteConfirmation} from 'commons/utils/Messagefunctions';
import {BeneficiaryInfo, MailAuthResponse, OtpValidation} from 'commons/types/Types';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useBeneficiaryContext} from 'context/beneficiary-context/BeneficiaryContext';
import {AlertActions, HistoryRoutes, Messages, SnackbarVariants} from 'commons/constants';
import {setTooltip} from 'hooks/useTooltip';
import {MainContent} from 'layouts/commons';

function Beneficiary() {
    const {
        showAlert,
        defaultError,
        genericError,
        showAlertError,
        closeAlert,
        actions,
        showCustomAlert,
    } = useAlert();
    const {
        setTempBeneficiary,
        beneficiaries,
        percentageCompleted,
        setActive,
        isActive,
        setBeneficiaries,
        authorization,
        setAuthorization,
        setPercentageCompleted,
    } = useBeneficiaryContext();
    const history = useHistory();
    const {enqueueSnackbar} = useSnackbar();
    const {flow, moduleStep, updateModuleStep} = useFlowContext();
    const {isAdmin, beneficiaryToken, setBeneficiaryToken} = useUserContext();
    const {menu, fatherKycId, setKycId, expedientId} = useMenuContext();

    const [aux, setAux] = React.useState<boolean>(false);
    const [open, setOpen] = React.useState<boolean>(false);
    const [toDelete, setToDelete] = React.useState<number>();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [showViews] = React.useState<{value: boolean}>({value: false});
    const [viewKey, setViewKey] = React.useState<string>(
        `View-${getRandomHex()}`
    );
    const views: Array<EfeViewsArray> = useViewSearch({...flow}, [...menu]);

    async function getOtp() {
        setIsLoading(true);
        try {
            const response: EfeResponse<MailAuthResponse> =
                await ClientService.getMailAuthentication(fatherKycId);
            if (response && response.data) {
                setOpen(!open);
                enqueueSnackbar(response.data.message, {
                    variant: SnackbarVariants.SUCCESS,
                });
            } else {
                enqueueSnackbar(Messages.OTP_AUTHORIZATION_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            showAlertError(error, Messages.OTP_AUTHORIZATION_ERROR);
        } finally {
            setIsLoading(false);
        }
    }

    async function handleOtp(values: string) {
        setIsLoading(true);
        try {
            const response: EfeResponse<OtpValidation> =
                await ClientService.validateMailAuthentication(fatherKycId, values);
            if (response && response.data && response.data.authenticated) {
                setBeneficiaryToken(true);
                setOpen(!open);
                enqueueSnackbar(Messages.OTP_AUTHORIZATION_Ok, {
                    variant: SnackbarVariants.SUCCESS,
                });
            } else {
                enqueueSnackbar(Messages.OTP_UNAUTHORIZED, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            showAlertError(error, Messages.OTP_AUTHORIZATION_ERROR);
        } finally {
            setIsLoading(false);
        }
    }

    function handleSubmit() {
        const path = nextStep(flow, menu, true);
        setKycId(-1);
        updateModuleStep(false);
        history.push(path);
    }

    async function handleAuthorization(event: React.ChangeEvent<HTMLInputElement>) {
        setIsLoading(true);
        const authorized = event.target.checked;
        try {
            const response: EfeResponse<SyncClientDataResponse> =
                await ClientService.updateAuthorization(expedientId, authorized);
            if (response && response.data) {
                enqueueSnackbar(response.data.message, {
                    variant: SnackbarVariants.SUCCESS,
                });
                setAuthorization(authorized);
            } else {
                enqueueSnackbar(Messages.AUTHORIZATION_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            showAlertError(error, Messages.AUTHORIZATION_ERROR);
        } finally {
            setIsLoading(false);
        }
    }

    function handleChange(kycIdSelected: number) {
        setKycId(kycIdSelected);
        showViews.value = true;
        setActive(true);
        history.push(HistoryRoutes.BENEFICIARY_INFO);
    }

    async function handleAddBeneficiary() {
        setKycId(-1);
        setAux(!aux);
        setTempBeneficiary(undefined);
        showViews.value = true;
        history.push(HistoryRoutes.BENEFICIARY_INFO);
        setViewKey(`View-${getRandomHex()}`);
        setActive(true);
    }

    async function getAuthorizationData() {
        setIsLoading(true);
        try {
            const response: EfeResponse<BeneficiariesAuthorization> =
                await ClientService.getBeneficiariesAuth(expedientId);
            if (response && response.data) {
                setAuthorization(response.data.isAuthorized);
            } else {
                setAuthorization(false);
            }
        } catch (error) {
            showAlertError(error, Messages.GET_AUTHORIZATION_ERROR);
        } finally {
            setIsLoading(false);
        }
    }

    async function getBeneficiaries() {
        setIsLoading(true);
        try {
            const response: EfeResponse<BeneficiariesResponse> =
                await ClientService.getBeneficiaries(fatherKycId);
            if (response && response.data) {
                const tmpRelateds: BeneficiaryInfo[] = [];
                let tmpPercentage = 0.0;
                showViews.value = false;
                for (const item of response.data.data) {
                    tmpPercentage = tmpPercentage + (item.participationPercent || 0);
                    tmpRelateds.push({
                        kycId: item.kycId || 0,
                        completeName: `${item.name} ${item.lastName} ${item.secondLastName}`,
                        participationPercent: item.participationPercent || 0,
                        foreignCitizen: item.foreignCitizen,
                    });
                }
                updateModuleStep(tmpPercentage * 1 >= 100 && authorization);
                setBeneficiaries(tmpRelateds);
                setPercentageCompleted(tmpPercentage);
            }
        } catch (error) {
            updateModuleStep(true);
            setBeneficiaries([]);
            setKycId(-1);
        } finally {
            setIsLoading(false);
        }
    }

    async function deleteBeneficiary(kycId: number) {
        setIsLoading(true);
        try {
            const response: EfeResponse<SyncClientDataResponse> =
                await ClientService.deleteBeneficiary(kycId);
            if (response && response.data) {
                enqueueSnackbar(response.data.message, {
                    variant: SnackbarVariants.SUCCESS,
                });
                getBeneficiaries();
            } else {
                enqueueSnackbar(Messages.ERROR_DELETE_SERVICE, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            showAlertError(error, Messages.ERROR_DELETE_SERVICE);
        } finally {
            setIsLoading(false);
        }
    }

    async function handleDelete(kycId: number, name: string) {
        setToDelete(kycId);
        showCustomAlert(deleteConfirmation('beneficiario', name), [
            AlertActions.OK,
            AlertActions.CANCEL,
        ]);
    }

    function handleAlertAction(action?: string) {
        if (undefined !== toDelete && 'ok' === action) {
            deleteBeneficiary(toDelete);
        } else {
            setToDelete(undefined);
        }
        closeAlert();
    }

    React.useEffect(() => {
        if (beneficiaries && beneficiaries.length && !isActive) {
            showViews.value = false;
            setViewKey(`View-${getRandomHex()}`);
        }
    }, [beneficiaries, isActive]);

    React.useEffect(() => {
        if (fatherKycId > 0 && !isActive) {
            getBeneficiaries();
            getAuthorizationData();
        }
    }, []);

    const BeneficiaryView = (
        <Fragment>
            <BeneficiaryHeader
                isLoading={isLoading}
                authorization={authorization}
                percentageCompleted={percentageCompleted}
                beneficiaries={beneficiaries}
                handleChange={handleChange}
                handleDelete={handleDelete}
                handleAuthorization={handleAuthorization}
            />
            <Divider variant='middle' />

            <Grid container direction='row' justifyContent='space-between' key={viewKey}>
                {(showViews.value || isActive) &&
                    views &&
                    views.length > 0 &&
                    views.map((view: EfeViewsArray) => {
                        return (
                            <Grid item xs={12} key={view.index}>
                                {view.view}
                            </Grid>
                        );
                    })}
            </Grid>

            <BeneficiaryFooter
                moduleStep={moduleStep}
                authorization={authorization}
                handleSubmit={handleSubmit}
                percentageCompleted={percentageCompleted}
                numberOfBeneficiaries={beneficiaries.length}
                handleAddBeneficiary={handleAddBeneficiary}
            />
        </Fragment>
    );

    return (
        <Fragment>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={handleAlertAction}
                    actions={actions}
                />
            )}
            {open && (
                <EfeModal
                    open={open}
                    title={'Doble factor de autenticación'}
                    sizeOverride={30}
                    reactElement={
                        <EfeOtp
                            title='Introduce el código que acabamos de enviar a tu correo electrónico'
                            length={6}
                            isPassword={false}
                            handleClick={handleOtp}
                        />
                    }
                    handleAction={() => setOpen(!open)}
                />
            )}
            {!beneficiaryToken && (
                <MainContent>
                    {setTooltip(
                        <Grid container direction='row' justifyContent='center' key={viewKey}>
                            <Grid item xs={4}>
                                <Box m={2}>
                                    <EfeButtonPrimary
                                        fullWidth
                                        onClick={getOtp}
                                        disabled={isAdmin()}>
                                        {'Registrar Beneficiarios'}
                                    </EfeButtonPrimary>
                                </Box>
                            </Grid>
                        </Grid>,
                        isAdmin(),
                        'Esta opci\u00F3n solo esta disponible para clientes.',
                        'bottom'
                    )}
                </MainContent>
            )}
            {beneficiaryToken && BeneficiaryView}
        </Fragment>
    );
}

export default Beneficiary;
